import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function RegisterPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    adeli: '',
    siret: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { signUp, loading } = useAuth();
  const navigate = useNavigate();

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (form.password.length < 6) {
      setSubmitError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setSubmitError('Les mots de passe ne correspondent pas.');
      return;
    }

    const { error } = await signUp(form.email, form.password, {
      first_name: form.firstName,
      last_name: form.lastName,
      adeli_number: form.adeli,
      siret: form.siret,
    });

    if (error) {
      setSubmitError(error.message);
    } else {
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
          </div>
          <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Inscription réussie !</h1>
          <p className="text-sm text-foreground-600">
            Votre compte a été créé avec succès. Vous allez être redirigé vers votre tableau de bord.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 md:px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold font-heading text-primary-600">CotIdel.</span>
          </Link>
          <h1 className="text-xl font-bold font-heading text-foreground-950 mt-4 mb-2">Créer un compte</h1>
          <p className="text-sm text-foreground-500">
            2 cotations offertes à la création de votre compte. Aucune carte bancaire requise.
          </p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{submitError}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                  Prénom
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={form.firstName}
                  onChange={(e) => update('firstName', e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Marie"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                  Nom
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={form.lastName}
                  onChange={(e) => update('lastName', e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Dupont"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="marie.dupont@email.fr"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="adeli" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                  N° ADELI
                </label>
                <input
                  id="adeli"
                  name="adeli"
                  type="text"
                  required
                  value={form.adeli}
                  onChange={(e) => update('adeli', e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="123456789"
                />
              </div>
              <div>
                <label htmlFor="siret" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                  N° SIRET
                </label>
                <input
                  id="siret"
                  name="siret"
                  type="text"
                  required
                  value={form.siret}
                  onChange={(e) => update('siret', e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="12345678900012"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="6 caractères minimum"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                Confirmer le mot de passe
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(e) => update('confirmPassword', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {/* Honeypot anti-spam */}
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="company-alt-field"
              value=""
              onChange={() => {}}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold px-4 py-3 rounded-md text-sm transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Création en cours...
                </span>
              ) : (
                'Créer mon compte'
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-background-200 text-center">
            <p className="text-xs text-foreground-500">
              Déjà inscrit ?{' '}
              <Link to="/auth/login" className="text-primary-600 font-semibold hover:text-primary-700">
                Se connecter
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center mt-6 text-xs text-foreground-400">
          <Link to="/" className="hover:text-foreground-600">
            <i className="ri-arrow-left-line mr-1"></i>
            Retour à l&apos;accueil
          </Link>
        </p>
      </div>
    </div>
  );
}